<template>
  <!-- Ширина зависит от фида: с фидом 1250px (основной экран + фид), без него 650px. Когда фида нет — см. positionPointer -->
  <VDialogV3
    ref="dialog"
    id="order-position-dialog"
    position="right"
    no-padding
    not-hide-on-click

    :width="positionPointer !== null ? '1250px' : '650px'"

    :tabs="tabs"
    :entity="localPosition"
    :feed-entity-pointer="positionPointer"

    v-model:active-tab="activeTab"

    show-edit-button
    hide-footer
    @edit-button:clicked="showRequisitesDialog"
    @close="onClose"
  >
    <template v-if="isQuote" #badges>
      <VBadge class="default">Свободный выкуп</VBadge>
    </template>

    <OrderPositionMainSection
      v-if="activeTab?.id === 'main'"
      :position="localPosition"
    />

    <OfferCharacteristicsSection
      v-if="activeTab?.id === 'characteristic'"
      :id="localPosition.offer.id"
    />

    <FilesList
      v-if="activeTab?.id === 'files' && isFilesAvailable"
      :entities="filesPointers"
      :inherited="inheritedFilesPointers"
    />
  </VDialogV3>
</template>

<style lang="scss" src="./OrderPositionCard.scss" />

<script lang="ts">
import EntityPointer               from '@/domains/EntityPointer/EntityPointer';
import OrderPosition               from '@/domains/Orders/OrderPosition';
import PreorderPosition            from '@/domains/Preorders/PreorderPosition';
import OrderPositionMainSection    from "@/domains/Orders/components/OrderPositionCard/OrderPositionMainSection.vue";
import OfferCharacteristicsSection from "@/domains/Offers/components/OfferCard/OfferCharacteristicsSection.vue";
import OrdersService               from "@/domains/Orders/OrdersService";

export default defineComponent({
  name: 'OrderPositionCard',

  components: {
    OrderPositionMainSection,
    OfferCharacteristicsSection
  },

  props: {
    /** ID позиции — диалог сам загрузит объект */
    id: {
      type: Number,
      default: null,
    },

    /** Нужен только при загрузке по id — бэк требует флаг */
    isPreorder: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localPosition: null as OrderPosition | PreorderPosition | null,

      activeTab: null as { id: string, name: string } | null,

      /** Реквизиты сохранялись — при закрытии хост должен обновиться */
      isChanged: false,
    };
  },

  async mounted() {
    await this.init();
  },

  computed: {
    /** Тип сущности позиции: позиция предзаказа либо позиция заказа. */
    positionTypeName(): string {
      if (this.isPreorder) {
        return 'preorderPosition';
      } else {
        return 'orderPosition';
      }
    },

    /**
     * Указатель на позицию для фида. Есть только у покупателя: фид по позициям показываем
     * только ему, потому что не уверены, может ли его читать поставщик. Без указателя
     * фида в карточке нет, и она сужается до основного экрана (см. width в шаблоне).
     */
    positionPointer(): EntityPointer | null {
      if (this.$core.currentPartner.isBuyer) {
        return EntityPointer.make(this.positionTypeName, this.id);
      } else {
        return null;
      }
    },

    /** Файлы доступны только покупателю. Правило одно на вкладку, компонент и указатели. */
    isFilesAvailable(): boolean {
      return this.$core.currentPartner.isBuyer;
    },

    /** Указатели на сущности, к которым цепляются файлы: только сама позиция. Пустые отсеиваются. */
    filesPointers(): EntityPointer[] {
      if (this.isFilesAvailable) {
        return EntityPointer.collect([
          EntityPointer.make(this.positionTypeName, this.id, this.localPosition?.toString()),
        ]);
      } else {
        return [];
      }
    },

    /** Указатели на сущности, чьи файлы показываются у позиции только на чтение. Пустые отсеиваются. */
    inheritedFilesPointers(): EntityPointer[] {
      if (this.isFilesAvailable) {
        return EntityPointer.collect([
          EntityPointer.make('order', this.localPosition?.order?.id, 'Заказ'),
          EntityPointer.make('demandRequest', this.localPosition?.demandRequest?.id, 'Заявка'),
          EntityPointer.make('demandOption', this.localPosition?.demandOption?.id, 'Вариант'),
          EntityPointer.make('demandV2', this.localPosition?.demandV2?.id, 'Потребность')
        ]);
      } else {
        return [];
      }
    },

    /** Предзаказ из тендера котировок (свободный выкуп) */
    isQuote(): boolean {
      return this.localPosition instanceof PreorderPosition && this.localPosition.isQuote === true;
    },

    tabs() {
      const tabs = [
        {id: 'main', name: 'Основное'},
      ];

      if (this.localPosition?.isOfferBased) {
        tabs.push({id: 'characteristic', name: 'Характеристики'});
      }

      if (this.isFilesAvailable) {
        tabs.push({id: 'files', name: 'Файлы'});
      }

      return tabs;
    },
  },

  methods: {
    async init(): Promise<void> {
      if (this.$core.utils.isNullOrUndefined(this.id) !== true) {
        this.localPosition = await OrdersService.getPosition(this.id, this.isPreorder, this.$core.currentUser.isAdmin);
      }
    },

    async showRequisitesDialog() {
      await this.$core.dialogs.OrderPositionRequisites.showAsPromise({
        position: this.localPosition,
      }, {
        save: async ({ position, reasonText }) => {
          const isOk = await OrdersService.updatePosition(position, reasonText)
          if (isOk === true) {
            this.isChanged = true;
            this.$core.dialogs.OrderPositionRequisites.resolve();
            this.init();
            this.$refs.dialog.renewFeed();
          }
        }
      })
    },

    onClose(): void {
      if (this.isChanged === true) {
        this.$core.dialogs.OrderPositionCard.resolve();
      }
      else {
        // ничего не сохраняли — диалог закроет сам VDialogV3
      }
    },
  },
});
</script>
